import React, { useEffect, useRef, useState } from "react";
import { HiFilter, HiSearch } from "react-icons/hi";
import axios from "axios";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";
import Swal from "sweetalert2";
import { FaFileExcel } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
const TransactionHistoriesTable = () => {
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("All Types");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showCustomDate, setShowCustomDate] = useState(false);
  const typeDropdownRef = useRef(null);
  const [transactionHistories, setTransactionHistories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeout = useRef(null);

  const toggleTypeDropdown = () => setIsTypeDropdownOpen(!isTypeDropdownOpen);

  const handleTypeClick = (typeOption, typeValue) => {
    setSelectedType(typeOption);
    setIsTypeDropdownOpen(false);

    // If user selects "Custom Date"
    if (typeOption === "Custom Date") {
      setShowCustomDate(true);
      setStartDate("");
      setEndDate("");

      // Clear type filters when switching to Custom Date
      setFilters((prev) => {
        const updated = { ...prev };
        delete updated.deductionType;
        delete updated.startDate;
        delete updated.endDate;
        return updated;
      });
    } else {
      // If user selects any other filter, clear custom dates
      setShowCustomDate(false);
      setStartDate("");
      setEndDate("");

      const newFilters = { ...filters };
      delete newFilters.startDate;
      delete newFilters.endDate;

      if (typeValue) newFilters.deductionType = typeValue;
      else delete newFilters.deductionType;

      setFilters(newFilters);
      setCurrentPage(1);
      getTransactionHistories(1, newFilters);
    }
  };

  const handleDateChange = (type, value) => {
    let newFilters = { ...filters };
    if (type === "startDate") {
      setStartDate(value);
      newFilters.startDate = value ? new Date(value).toISOString() : undefined;
    } else if (type === "endDate") {
      setEndDate(value);
      newFilters.endDate = value ? new Date(value).toISOString() : undefined;
    }
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClickOutside = (event) => {
    if (
      typeDropdownRef.current &&
      !typeDropdownRef.current.contains(event.target)
    ) {
      setIsTypeDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    getTransactionHistories(currentPage, filters);
  }, [currentPage, filters]);

  const getTransactionHistories = async (page = 1, filters = {}) => {
    try {
      const params = new URLSearchParams({ page });
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
      if (filters.deductionType)
        params.append("deductionType", filters.deductionType);
      if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);

      setLoading(true);
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/coin-deduction/get-deductions?${params.toString()}`
      );
      setTransactionHistories(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setCurrentPage(response.data.pagination.currentPage);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching transaction histories:", error);
      setLoading(false);
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      const newFilters = { ...filters };
      if (value) newFilters.searchTerm = value;
      else delete newFilters.searchTerm;

      setFilters(newFilters);
      setCurrentPage(1);
    }, 500);
  };

  const handleExport = async () => {
    try {
      if (!filters.startDate || !filters.endDate) {
        Swal.fire({
          icon: "error",
          title: "Export Failed",
          text: "Please give the start and end date",
        });
        return;
      }
      Swal.fire({
        title: "Preparing export...",
        text: "Please wait while we generate the Excel file",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const payload = {};

      if (filters.startDate) payload.startDate = filters.startDate;
      if (filters.endDate) payload.endDate = filters.endDate;
      if (filters.deductionType) payload.deductionType = filters.deductionType;
      if (filters.searchTerm) payload.searchTerm = filters.searchTerm;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/coin-deduction/export`,
        payload,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Try to read filename from header
      const disposition = response.headers["content-disposition"];
      let fileName = "coin-history.xlsx";

      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) fileName = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
      Swal.close();
    } catch (error) {
      Swal.close();
      console.error("Export failed:", error);

      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: "Unable to export transaction history",
      });
    }
  };

  return (
    <div className="py-5">
      <div className="flex flex-col items-center justify-start gap-2 space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
        {/* Type Dropdown */}
        <div
          className="md:min-w-1/5 relative w-full md:w-1/5"
          ref={typeDropdownRef}
        >
          <button
            className="flex w-full items-center rounded-md border bg-white px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
            onClick={toggleTypeDropdown}
          >
            <HiFilter color="#c51369" className="mr-2 h-5 w-5" />
            {selectedType}
          </button>

          {isTypeDropdownOpen && (
            <div className="absolute left-0 z-10 mt-2 w-full rounded-md border bg-white shadow-lg">
              <ul className="py-2">
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => {
                    handleTypeClick("All Types", null);
                    setShowCustomDate(false);
                  }}
                >
                  All Types
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => {
                    handleTypeClick("Gift", "gift");
                    setShowCustomDate(false);
                  }}
                >
                  Gift
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => {
                    handleTypeClick("Video Call", "videoCall");
                    setShowCustomDate(false);
                  }}
                >
                  Video Call
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => {
                    handleTypeClick("Audio Call", "audioCall");
                    setShowCustomDate(false);
                  }}
                >
                  Audio Call
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleTypeClick("Custom Date", null)}
                >
                  Custom Date
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Show Custom Date Fields outside dropdown */}
        {showCustomDate && (
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                className="rounded-md border px-3 py-2 text-gray-700 focus:ring-1 focus:ring-[#c51369]"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange("endDate", e.target.value)}
                className="rounded-md border px-3 py-2 text-gray-700 focus:ring-1 focus:ring-[#c51369]"
              />
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="relative  md:w-1/4">
          <HiSearch className="absolute left-3 top-3 text-gray-400" size={20} />

          <input
            type="text"
            placeholder="Search by number..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full rounded-md border px-10 py-2 pr-10 text-gray-700 focus:ring-1 focus:ring-[#c51369]"
          />

          {searchTerm && (
            <MdCancel
              className="absolute right-3 top-3 z-10 cursor-pointer text-gray-400 hover:text-gray-600"
              size={20}
              onClick={() => {
                setSearchTerm("");
                const newFilters = { ...filters };
                delete newFilters.searchTerm;
                setFilters(newFilters);
                setCurrentPage(1);
              }}
            />
          )}
        </div>

        {/* Export Button */}
        {showCustomDate && (
          <button
            onClick={handleExport}
            className="flex items-center gap-2 rounded-md bg-[#c51369] px-4 py-2 text-white hover:bg-[#a01052]"
          >
            <FaFileExcel /> Export
          </button>
        )}
      </div>

      {loading ? (
        <TableLoader />
      ) : (
        <div className="mx-auto mt-5 w-auto rounded-t-2xl">
          <div className="overflow-x-auto rounded-t-2xl">
            <table className="w-full rounded-t-2xl text-left text-sm">
              <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                <tr className="text-center">
                  <th className="px-6 py-3">Sl No.</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Time</th>
                  <th className="px-6 py-3">User</th>
                  <th className="px-6 py-3">Receiver</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Type</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {transactionHistories?.map((transaction, index) => (
                  <tr
                    key={transaction?._id}
                    className="text-md bg-white tracking-wide text-gray-700"
                  >
                    <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                      {(currentPage - 1) * 5 + index + 1}
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                      {new Date(transaction?.createdAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                      {new Date(transaction?.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: false,
                      })}
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                      <div className="flex">
                        <img
                          src={transaction?.userId?.selected_Avatar?.avatarUrl}
                          alt="user avatar"
                          className="h-10 w-10 rounded-full"
                        />
                        <div className="flex flex-col items-center justify-center">
                          <span className="ml-4">
                            {transaction?.userId?.fullName}
                          </span>
                          <span className="ml-4">
                            +91 {transaction?.userId?.phone.slice(2) || "NA"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                      <div className="flex items-center">
                        <img
                          src={
                            transaction?.receiverId?.selected_Avatar?.avatarUrl
                          }
                          alt="receiver avatar"
                          className="h-10 w-10 rounded-full"
                        />
                        <div className="flex flex-col items-center justify-center">
                          <span className="ml-4">
                            {transaction?.receiverId?.fullName}
                          </span>
                          <span className="ml-4">
                            +91{" "}
                            {transaction?.receiverId?.phone.slice(2) || "NA"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                      ₹{transaction?.deductionAmount}
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4 font-bold">
                      {transaction?.deductionType.toUpperCase()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={paginate}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionHistoriesTable;
